import memoize, { memoizeClear } from "@-/com.npmjs.memoize";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * How long the boot shell (and with it the native splash) waits for `ChromeReadyMarker` before
 * failing open.
 *
 * The marker is the fast path and fires on the first committed frame of the chrome, which is well
 * under a second after the application module mounts even on a slow Android device. The deadline is
 * the guarantee: a route that never mounts the marker (an error fallback, a surface outside `(nav)`,
 * a Learn state where the summary is not focused, a future screen nobody taught about the marker)
 * would otherwise leave the user staring at the splash logo forever with no way to tell anything is
 * wrong. Ten seconds is generous enough that no healthy launch reaches it; past it, revealing the
 * app over a possibly half-drawn shell is strictly better than never revealing it. The expiry is
 * reported (`onDeadlineExceeded` → Sentry) so a missing marker is found and fixed, not hidden.
 */
export const APPLICATION_VISUAL_READY_DEADLINE_MS = 10_000;

interface ApplicationVisualReadyState {
  listeners: Set<() => void>;
  ready: boolean;
}

/**
 * One local handoff signal for the lifetime of the JS runtime.
 *
 * The native boot shell stays mounted above Expo Router until a real destination's passive effect
 * confirms that its visible frame committed. An explicit signal (rather than inferred loading
 * state) keeps network, auth, queries, and connectivity out of the decision completely; the
 * deadline above only bounds the case where the signal is never raised.
 */
const getApplicationVisualReadyState = memoize((): ApplicationVisualReadyState => ({
  listeners: new Set(),
  ready: false,
}));

export function isApplicationVisualReady(): boolean {
  return getApplicationVisualReadyState().ready;
}

export function markApplicationVisualReady(): void {
  const state = getApplicationVisualReadyState();
  if (state.ready) {
    return;
  }

  state.ready = true;
  // Each listener's cleanup deletes it from the set mid-iteration, which Set iteration allows.
  for (const listener of state.listeners) {
    listener();
  }
  state.listeners.clear();
}

/**
 * Resolves once a routed destination has committed its visible local frame — or, failing open,
 * once {@link APPLICATION_VISUAL_READY_DEADLINE_MS} has passed without that signal.
 */
export function whenApplicationVisualReady(): Promise<void> {
  const state = getApplicationVisualReadyState();
  if (state.ready) {
    return Promise.resolve();
  }
  return boundedPromise(
    {
      deadline: APPLICATION_VISUAL_READY_DEADLINE_MS,
      onDeadline: "resolve",
      operation: "app chrome ready marker (ChromeReadyMarker)",
    },
    (resolve) => {
      const listener = () => resolve();
      state.listeners.add(listener);
      return () => {
        state.listeners.delete(listener);
      };
    },
  );
}

/** Test-only: make the next rendered application tree perform a fresh visual handoff. */
export function resetApplicationVisualReadyForTests(): void {
  memoizeClear(getApplicationVisualReadyState);
}
